import { useState, useRef, useEffect } from 'react';
import { APP } from './tokens';

interface InlineCommentFormProps {
  /** Pre-filled quote from text selection */
  quote?: string;
  onSubmit: (body: string) => void;
  onCancel: () => void;
}

const F = APP.fontFamily;

/**
 * Inline comment creation form — replaces browser prompt().
 * Shows optional quote block and text input.
 * Rendered inside DocumentColumn at the comment anchor point.
 */
export default function InlineCommentForm({ quote, onSubmit, onCancel }: InlineCommentFormProps) {
  const [body, setBody] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    console.log('[InlineCommentForm] Mounted', { hasQuote: !!quote });
    // Auto-focus the textarea on mount
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCancel(); return; }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleSubmit = () => {
    if (!body.trim()) return;
    onSubmit(body.trim());
  };

  return (
    <div
      style={{
        width: 280,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        border: `1px solid ${APP.border}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        fontFamily: F,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Quote block */}
      {quote && (
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${APP.border}`, backgroundColor: '#FAF7F4' }}>
          <span
            style={{
              fontSize: 9,
              fontWeight: 500,
              letterSpacing: '0.05em',
              color: '#D97645',
              textTransform: 'uppercase' as const,
              fontFamily: F,
              display: 'block',
              paddingBottom: 4,
            }}
          >
            Quote
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: '#6B5E52',
              lineHeight: '16px',
              fontFamily: '"Georgia", serif',
              borderLeft: '2px solid #D97645',
              paddingLeft: 8,
              display: 'block',
            }}
          >
            {quote.length > 150 ? quote.slice(0, 150) + '…' : quote}
          </span>
        </div>
      )}

      {/* Input area */}
      <div style={{ padding: 12 }}>
        <textarea
          ref={inputRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Add a comment…"
          rows={2}
          style={{
            width: '100%',
            resize: 'none',
            border: `1px solid ${APP.border}`,
            borderRadius: 6,
            padding: '8px 10px',
            fontFamily: F,
            fontSize: 12,
            lineHeight: '16px',
            color: '#2E2318',
            outline: 'none',
            backgroundColor: '#FDFCFB',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, paddingTop: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: `1px solid ${APP.border}`,
              backgroundColor: 'transparent',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#6B5E52',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!body.trim()}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: body.trim() ? '#D97645' : '#D9764580',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#FFFFFF',
              cursor: body.trim() ? 'pointer' : 'default',
            }}
          >
            Comment
          </button>
        </div>
      </div>
    </div>
  );
}
